import { BasicUserGroupDto } from "../../types/admin/user-groups/BasicUserGroupDto";
import { CustomFieldOption } from "../../types/admin/user-groups/CustomFieldOption";
import { UserGroupDto } from "../../types/admin/user-groups/UserGroupDto";
import { UserGroupForm } from "../../types/admin/user-groups/UserGroupForm";
import UserGroupListDataResponse from "../../types/dtos/admin/user-groups/UserGroupListDataResponse";
import UserGroupListInitialResponse from "../../types/dtos/admin/user-groups/UserGroupListInitialResponse";
import ResultStatus from "../../types/dtos/generic/ResultStatus";
import { apiClient } from "../apiClient";

interface JobTitleSearchResult {
  id: number;
  name: string;
}

// ---------------------------------------------
// |                  API                      |
// ---------------------------------------------
class userGroupApi {
  constructor(authToken: string | undefined) {
    this.accessToken = authToken;
  }

  accessToken: string | undefined;

  // |-------------------------------------------|
  // |               LIST PAGE                   |
  // |-------------------------------------------|

  /** Gets the initial user group list page data */
  getInitialUserGroupListData(
    onSuccess: (response: UserGroupListInitialResponse) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/user-group/get-initial-user-group-list-data`
    ).then(
      (response) => {
        onSuccess(response as UserGroupListInitialResponse);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Gets the list data for the given group id */
  getUserGroupListDataForGroupId(
    id: number,
    onSuccess: (response: UserGroupListDataResponse) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/user-group/get-user-group-list-data-for-group-id?groupId=${id}`
    ).then(
      (response) => {
        onSuccess(response as UserGroupListDataResponse);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** GET xlsx blob of all active members in the user group */
  exportGroupMembersSpreadsheet(groupId: number): Promise<Blob> {
    return apiClient(
      this.accessToken,
      `dashboards/user-group/export-members-spreadsheet?groupId=${groupId}`,
      { method: "GET" },
      "BLOB"
    ) as Promise<Blob>;
  }

  /** GET xlsx blob of group rules for all accessible groups (manage permission required) */
  exportGroupsRulesSpreadsheet(): Promise<Blob> {
    return apiClient(
      this.accessToken,
      `dashboards/user-group/export-groups-rules-spreadsheet`,
      { method: "GET" },
      "BLOB"
    ) as Promise<Blob>;
  }

  /** Delete a user group */
  deleteUserGroup(
    groupId: number,
    onSuccess: () => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/user-group/delete?groupId=${groupId}`,
      {
        method: "DELETE",
      }
    ).then(
      () => {
        onSuccess();
      },
      (error) => {
        onError(error);
      }
    );
  }

  // |-------------------------------------------|
  // |               MANAGE PAGE                 |
  // |-------------------------------------------|
  /** Create a new user group */
  createUserGroup(
    groupData: UserGroupForm,
    onSuccess: (data: boolean) => void,
    onError: (error: any) => void
  ) {
    return apiClient(this.accessToken, "dashboards/user-group/create", {
      body: JSON.stringify(groupData),
    }).then(
      (data) => {
        onSuccess(data as boolean);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Update an existing user group */
  updateUserGroup(
    groupId: number,
    groupData: UserGroupForm,
    onSuccess: (data: ResultStatus) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/user-group/update?groupId=${groupId}`,
      {
        body: JSON.stringify(groupData),
      }
    ).then(
      (data) => {
        onSuccess(data as ResultStatus);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Load a specific user group by ID */
  loadUserGroup(
    groupId: number,
    onSuccess: (data: UserGroupDto) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/user-group/get?groupId=${groupId}`
    ).then(
      (data) => {
        onSuccess(data as UserGroupDto);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Get job levels */
  getJobLevels(
    onSuccess: (data: { id: number; name: string }[]) => void,
    onError: () => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/appraisal-level/get-appraisal-levels-by-client-id`
    ).then(
      (data) => {
        onSuccess(data as JobTitleSearchResult[]);
      },
      (error) => {
        onError();
      }
    );
  }

  getJobTitles(
    onSuccess: (data: JobTitleSearchResult[]) => void,
    onError: () => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/job-title/get-active-job-titles-by-client-id`
    ).then(
      (data) => {
        onSuccess(data as JobTitleSearchResult[]);
      },
      (error) => {
        onError();
      }
    );
  }

  getCustomFieldOptions(
    onSuccess: (data: CustomFieldOption[]) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      "dashboards/user-group/get-custom-field-options"
    ).then(
      (data) => {
        onSuccess(data as CustomFieldOption[]);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Distinct location FullName values from ClientLocationCache (typeahead + browse). */
  searchLocationNames(query: string, limit = 50) {
    const q = encodeURIComponent(query ?? "");
    return apiClient(
      this.accessToken,
      `dashboards/user-group/search-location-names?q=${q}&limit=${limit}`
    ) as Promise<string[]>;
  }

  /** Distinct values for a custom field column (1–10) for the current client. */
  searchCustomFieldValues(
    fieldNumber: number,
    query: string,
    limit = 50
  ) {
    const q = encodeURIComponent(query ?? "");
    return apiClient(
      this.accessToken,
      `dashboards/user-group/search-custom-field-values?fieldNumber=${fieldNumber}&q=${q}&limit=${limit}`
    ) as Promise<string[]>;
  }

  getAnalyticsUserGroups(
    onSuccess: (data: BasicUserGroupDto[]) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      "dashboards/user-group/get-analytics-user-groups"
    ).then(
      (data) => {
        onSuccess(data as BasicUserGroupDto[]);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Get user groups available for journey sending */
  getUserGroupsForJourneySending(
    onSuccess: (data: BasicUserGroupDto[]) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      "dashboards/user-group/get-user-groups-for-journey-sending"
    ).then(
      (data) => {
        onSuccess(data as BasicUserGroupDto[]);
      },
      (error) => {
        onError(error);
      }
    );
  }

  // |-------------------------------------------|
  // |          MEMBER EXCLUSION                 |
  // |-------------------------------------------|

  /** Exclude a member from a user group */
  excludeMember(
    groupId: number,
    memberId: number,
    reason: string,
    onSuccess: (result: ResultStatus) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/user-group/exclude-member?groupId=${groupId}&memberId=${memberId}`,
      {
        method: "POST",
        body: JSON.stringify({ reason }),
      }
    ).then(
      (response) => {
        onSuccess(response as ResultStatus);
      },
      (error) => {
        onError(error);
      }
    );
  }

  /** Restore a member to a user group */
  restoreMember(
    groupId: number,
    memberId: number,
    onSuccess: (result: ResultStatus) => void,
    onError: (error: any) => void
  ) {
    return apiClient(
      this.accessToken,
      `dashboards/user-group/restore-member?groupId=${groupId}&memberId=${memberId}`,
      {
        method: "POST",
      }
    ).then(
      (response) => {
        onSuccess(response as ResultStatus);
      },
      (error) => {
        onError(error);
      }
    );
  }
}

export type { JobTitleSearchResult };
export default userGroupApi;
